import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button as BootstrapButton } from "reactstrap";
import { Badge } from "./Badge";
import './Button.scss';

/**
 * Button component for triggering user actions
 * 
 * @component
 * @example
 * <Button variant="primary" onClick={() => console.log('Clicked!')}>Save</Button>
 * <Button variant="danger" badgeValue="8" badgeColor="warning">Notifications</Button>
 */
export const Button = ({ 
  variant = 'primary', 
  badgeColor = 'default', 
  badgeValue = null, 
  children, 
  disabled = false, 
  className = '', 
  size = 'normal',
  ...rest 
}) => {
  
  const buttonClasses = classNames(
    className,
    'btn-bold',
    {
      'px-0 py-1': variant === 'link',
      'custom-xs-button': size === 'xs'
    }
  );
  
  return (
    <BootstrapButton
      className={buttonClasses}
      type="button"
      color={variant}
      disabled={disabled}
      size={size === 'normal' ? undefined : size === 'xs' ? undefined : size}
      {...rest}
    >
      {children}
      {badgeValue && (
        <Badge 
          style={{marginLeft: 0, top: "-24px", right: "0px"}} 
          color={badgeColor} 
          type="floating"
        >
          {badgeValue}
        </Badge>
      )}
    </BootstrapButton>
  );
};

Button.propTypes = {
  /** Button color variant */
  variant: PropTypes.oneOf(['primary', 'secondary', 'default', 'info', 'success', 'danger', 'warning', 'link', 'light', 'lighter']),
  /** Badge element color */
  badgeColor: PropTypes.oneOf(['primary', 'secondary', 'default', 'info', 'success', 'danger', 'warning', 'light']),
  /** Badge element value/content */
  badgeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Button content */
  children: PropTypes.node,
  /** Is button disabled */
  disabled: PropTypes.bool,
  /** Additional classes for button */
  className: PropTypes.string,
  /** Button size */
  size: PropTypes.oneOf(['xs', 'sm', 'normal', 'lg']),
  /** Click event handler */
  onClick: PropTypes.func
};

Button.defaultProps = {
  badgeColor: "default",
  badgeValue: null,
  onClick: undefined,
  size: 'normal',
  variant: 'primary',
};
